import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { GLOSSARY_TERMS } from '../../data/glossary';
import { getPatternById } from '../../data';
import { useReveal } from '../../hooks/useReveal';
import styles from './Glossary.module.css';

const ALL_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const ACTIVE_LETTERS = new Set(
  GLOSSARY_TERMS.map(t => t.term[0].toUpperCase())
);

function scrollToLetter(letter: string) {
  const el = document.getElementById(`letter-${letter}`);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function Glossary() {
  const [query, setQuery] = useState('');
  useReveal([query]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? GLOSSARY_TERMS.filter(
          t =>
            t.term.toLowerCase().includes(q) ||
            t.definition.toLowerCase().includes(q)
        )
      : GLOSSARY_TERMS;

    const groups = new Map<string, typeof GLOSSARY_TERMS>();
    for (const term of filtered) {
      const letter = term.term[0].toUpperCase();
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter)!.push(term);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [query]);

  const totalFiltered = grouped.reduce((n, [, terms]) => n + terms.length, 0);
  const isSearching = query.trim().length > 0;

  return (
    <main className={styles.main}>

      {/* Hero */}
      <section className={styles.hero}>
        <div className="container">
          <div className={styles.heroInner}>
            <h1 className={`${styles.title} rise`}>Glossary</h1>
            <p className={`${styles.subtitle} page-intro`}>Key terms in AX and AI interface design</p>
            <div className={styles.searchWrap}>
              <svg
                className={styles.searchIcon}
                width="16" height="16" viewBox="0 0 24 24"
                fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                className={styles.searchInput}
                type="search"
                placeholder="Search terms or definitions…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                aria-label="Search glossary"
              />
              {query && (
                <button
                  className={styles.clearBtn}
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Alphabet nav — hidden during search */}
      {!isSearching && (
        <div className={styles.alphaWrap}>
          <div className="container">
            <div className={styles.alphaNav} role="navigation" aria-label="Jump to letter">
              {ALL_LETTERS.map(letter => (
                ACTIVE_LETTERS.has(letter) ? (
                  <button
                    key={letter}
                    className={`${styles.alphaBtn} ${styles.alphaBtnActive}`}
                    onClick={() => scrollToLetter(letter)}
                    aria-label={`Jump to ${letter}`}
                  >
                    {letter}
                  </button>
                ) : (
                  <span key={letter} className={styles.alphaBtn} aria-hidden="true">
                    {letter}
                  </span>
                )
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Terms */}
      <div className="container">
        <div className={styles.content}>

          {isSearching && (
            <p className={styles.searchMeta}>
              {totalFiltered === 0
                ? `No terms matching "${query}"`
                : `${totalFiltered} ${totalFiltered === 1 ? 'term' : 'terms'} matching "${query}"`}
            </p>
          )}

          {grouped.length === 0 && isSearching && (
            <div className={styles.empty}>
              <p>Try a different search term.</p>
              <button className={styles.emptyReset} onClick={() => setQuery('')}>
                Clear search
              </button>
            </div>
          )}

          {grouped.map(([letter, terms]) => (
            <section
              key={letter}
              id={`letter-${letter}`}
              className={styles.letterSection}
            >
              {!isSearching && (
                <div className={styles.letterHeading} aria-hidden="true">
                  {letter}
                </div>
              )}
              <div className={styles.termsList}>
                {terms.map(item => {
                  const resolvedRelated = item.related
                    .map(slug => ({ slug, pattern: getPatternById(slug) }))
                    .filter(({ pattern }) => pattern !== undefined);

                  return (
                    <article key={item.term} className={styles.termCard} data-reveal>
                      <h2 className={styles.termName}>{item.term}</h2>
                      <p className={styles.termDef}>{item.definition}</p>
                      {resolvedRelated.length > 0 && (
                        <div className={styles.related}>
                          <span className={styles.relatedLabel}>Related patterns</span>
                          <div className={styles.relatedLinks}>
                            {resolvedRelated.map(({ slug, pattern }) => (
                              <Link
                                key={slug}
                                to={`/pattern/${slug}`}
                                className={styles.relatedLink}
                              >
                                {pattern!.title}
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          ))}

        </div>
      </div>
    </main>
  );
}
